import { useSaleHasServiceOrder } from '../../../../useSaleHasServiceOrder'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import {
  CurrentView,
  useLineItemFulfillmentContext,
} from '../LineItemFulfillmentContext'
import {
  useLineItemFulfillmentContinueButtonStatus,
  useLineItemFulfillmentCreateNewCustomerFlow,
  useLineItemFulfillmentCustomerDetailsChange,
  useLineItemFulfillmentDecisionFlow,
  useLineItemFulfillmentDetailsChange,
  useLineItemFulfillmentReviewContactInfoFlow,
  useLineItemFulfillmentSaleCustomer,
} from '../hooks'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../hooks/useFulfillmentHelper'

type UseLineItemFulfilmentPopoverModelProps = {
  onFulfilmentDetailsSet: (fulfilmentDetails: FulfilmentDetail) => void
  onClose: () => void
  breakdownOuterPacks?: () => Promise<boolean>
}

export const useShouldShowWidePopover = () => {
  const { currentView, fulfilmentDetails } = useLineItemFulfillmentContext()
  const { getFulfillmentAddressStatus } = useFulfillmentHelper()
  const status = getFulfillmentAddressStatus(fulfilmentDetails)

  const isEmptyShippingAndNotEmptyBillingAddress =
    status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
    status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING

  return (
    currentView === CurrentView.FIX_SALE_ADDRESSES &&
    isEmptyShippingAndNotEmptyBillingAddress
  )
}

export const useLineItemFulfilmentPopoverModel = ({
  onFulfilmentDetailsSet,
  onClose,
  breakdownOuterPacks,
}: UseLineItemFulfilmentPopoverModelProps) => {
  const saleHasServiceOrder = useSaleHasServiceOrder()

  const {
    currentView,
    currentDecision,
    currentCustomer,
    customerGroup,
    isEmailInvalid,
    fulfilmentDetails,
    onSelectFulfilmentType,
  } = useLineItemFulfillmentContext()

  const {
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneNumberChange,
  } = useLineItemFulfillmentCustomerDetailsChange()

  const {
    handleSelectFulfillmentType,
    handleNoteChange,
  } = useLineItemFulfillmentDetailsChange()

  const {
    isContinueButtonDisabled,
  } = useLineItemFulfillmentContinueButtonStatus()

  const {
    onAddNewCustomer,
    onSelectCustomer,
    onRemoveCustomer,
  } = useLineItemFulfillmentSaleCustomer()

  const {
    handleContinueFromCreateNewCustomerView,
  } = useLineItemFulfillmentCreateNewCustomerFlow({
    onClose,
    saleHasServiceOrder,
  })
  const {
    handleContinueFromReviewContactInfoView,
  } = useLineItemFulfillmentReviewContactInfoFlow({
    onClose,
    saleHasServiceOrder,
  })

  const {
    handleContinueFromDecisionView,
    handleDecisionSelect,
  } = useLineItemFulfillmentDecisionFlow({
    onFulfilmentDetailsSet,
    onClose,
    onBreakdownOuterPacks: breakdownOuterPacks,
    saleHasServiceOrder,
  })

  const onClickContinue = () => {
    switch (currentView) {
      case CurrentView.DECISION:
        handleContinueFromDecisionView()
        break
      case CurrentView.REVIEW_CONTACT_INFO:
        handleContinueFromReviewContactInfoView()
        break
      case CurrentView.CREATE_NEW_CUSTOMER:
        handleContinueFromCreateNewCustomerView()
        break
      case CurrentView.FULFILMENT_METHOD:
        if (fulfilmentDetails.type === FulfilmentType.DELIVERY) {
          handleContinueFromReviewContactInfoView(fulfilmentDetails)
        } else {
          if (onSelectFulfilmentType) {
            onSelectFulfilmentType(fulfilmentDetails.type)
          }
          onFulfilmentDetailsSet(fulfilmentDetails)
          onClose()
        }
        break
      default:
        break
    }
  }

  return {
    currentView,
    currentDecision,
    currentCustomer,
    customerGroup,
    isEmailInvalid,
    fulfilmentDetails,
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneNumberChange,
    handleSelectFulfillmentType,
    handleNoteChange,
    isContinueButtonDisabled,
    onAddNewCustomer,
    onSelectCustomer,
    onRemoveCustomer,
    handleDecisionSelect,
    onClickContinue,
    saleHasServiceOrder,
  }
}
